import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Boxes,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Download,
  Eye,
  FileCheck2,
  PackageCheck,
  PackageX,
  RefreshCcw,
  Search,
  Truck,
  XCircle
} from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './BonsLivraisonDashboard.css';

const API_URL = import.meta.env.VITE_API_URL;

const emptyFilters = {
  jour: '',
  dateDebut: '',
  dateFin: '',
  statut: '',
  clientId: '',
  dossierId: '',
  chauffeur: '',
  matriculeVoiture: '',
  produit: ''
};

const statusLabels = {
  en_preparation: 'En preparation',
  en_cours: 'En cours',
  livre: 'Livre',
  annule: 'Annule',
  reaffecte: 'Reaffecte',
  brouillon: 'Brouillon',
  en_attente: 'En attente',
  valide: 'Valide',
  supprime: 'Supprime'
};

const fmtNumber = value => (Number(value) || 0).toLocaleString('fr-FR');
const fmtDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR');
};

const tokenHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const KpiCard = ({ icon: Icon, label, value, tone }) => (
  <div className={`bld-kpi ${tone || ''}`}>
    <span className="bld-kpi-icon"><Icon size={19} /></span>
    <span>{label}</span>
    <strong>{fmtNumber(value)}</strong>
  </div>
);

const BonsLivraisonDashboard = ({ onNavigate, onLogout, currentPage }) => {
  const [filters, setFilters] = useState(emptyFilters);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (String(value || '').trim()) params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/documents-financiers/bons-livraison/dashboard?${query}`, {
        headers: tokenHeaders()
      });
      setDashboard(response.data);
    } catch (err) {
      console.error('Erreur dashboard BL:', err);
      setError(err.response?.data?.message || 'Impossible de charger le dashboard Bons de Livraison.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const refreshAll = async () => {
    await loadDashboard();
  };

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const validateDocument = async (doc) => {
    if (!doc?._id) return;
    if (!window.confirm(`Valider le Bon de Livraison ${doc.numero} ?`)) return;
    try {
      await axios.patch(`${API_URL}/documents-financiers/${doc._id}/valider`, {}, { headers: tokenHeaders() });
      await refreshAll();
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors?.length ? errors.join('\n') : (err.response?.data?.message || 'Validation impossible.'));
    }
  };

  const cancelDocument = async (doc) => {
    if (!doc?._id) return;
    const motif = window.prompt('Motif de l annulation du BL');
    if (!motif) return;
    try {
      await axios.patch(`${API_URL}/documents-financiers/bons-livraison/${doc._id}/annuler`, { motif }, { headers: tokenHeaders() });
      await refreshAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Annulation impossible.');
    }
  };

  const openPdf = (doc) => {
    if (!doc?._id) return;
    window.open(`${API_URL}/documents-financiers/${doc._id}/pdf?token=${localStorage.getItem('token')}`, '_blank');
  };

  const openDetailPage = (doc) => {
    if (!doc?._id) return;
    if (onNavigate) {
      onNavigate(`detail-bon-livraison/${doc._id}`);
    } else {
      window.location.href = `/detail-bon-livraison/${doc._id}`;
    }
  };

  const stats = dashboard?.stats || {};
  const documents = dashboard?.documents || [];

  return (
    <div className="bld-layout">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <main className="bld-content">
        <section className="bld-header">
          <div>
            <span>Logistique clients</span>
            <h1>Bons de Livraison</h1>
            <p>Suivi, validation, annulation, reaffectation et controle des produits livres.</p>
          </div>
          <button className="bld-icon-btn primary" onClick={refreshAll} title="Actualiser">
            <RefreshCcw size={18} />
          </button>
        </section>

        {loading ? (
          <LoadingSpinner message="Chargement des Bons de Livraison..." />
        ) : error ? (
          <div className="bld-error">{error}</div>
        ) : (
          <>
            <section className="bld-kpis">
              <KpiCard icon={ClipboardList} label="Total BL" value={stats.totalBL} tone="blue" />
              <KpiCard icon={CalendarDays} label="Crees aujourd'hui" value={stats.creesAujourdhui} tone="slate" />
              <KpiCard icon={AlertTriangle} label="En attente validation" value={stats.enAttenteValidation} tone="amber" />
              <KpiCard icon={FileCheck2} label="Valides" value={stats.valides} tone="green" />
              <KpiCard icon={Truck} label="En cours livraison" value={stats.enCoursLivraison} tone="purple" />
              <KpiCard icon={PackageCheck} label="Livres" value={stats.livres} tone="teal" />
              <KpiCard icon={XCircle} label="Annules" value={stats.annules} tone="red" />
              <KpiCard icon={RefreshCcw} label="Reaffectes" value={stats.reaffectes} tone="indigo" />
              <KpiCard icon={Boxes} label="Produits a livrer" value={stats.totalProduitsALivrer} tone="blue" />
              <KpiCard icon={CheckCircle2} label="Produits livres" value={stats.produitsLivres} tone="green" />
              <KpiCard icon={PackageX} label="Non installes" value={stats.produitsNonInstalles} tone="amber" />
              <KpiCard icon={AlertTriangle} label="Defectueux / perdus" value={(stats.produitsDefectueux || 0) + (stats.produitsPerdusCasses || 0)} tone="red" />
            </section>

            <section className="bld-filters">
              <label>Jour<input type="date" value={filters.jour} onChange={e => updateFilter('jour', e.target.value)} /></label>
              <label>Debut<input type="date" value={filters.dateDebut} onChange={e => updateFilter('dateDebut', e.target.value)} /></label>
              <label>Fin<input type="date" value={filters.dateFin} onChange={e => updateFilter('dateFin', e.target.value)} /></label>
              <label>Statut
                <select value={filters.statut} onChange={e => updateFilter('statut', e.target.value)}>
                  <option value="">Tous</option>
                  <option value="en_preparation">En preparation</option>
                  <option value="en_cours">En cours</option>
                  <option value="livre">Livre</option>
                  <option value="annule">Annule</option>
                  <option value="reaffecte">Reaffecte</option>
                  <option value="valide">Document valide</option>
                  <option value="brouillon">Brouillon</option>
                </select>
              </label>
              <label>Client ID<input value={filters.clientId} onChange={e => updateFilter('clientId', e.target.value)} placeholder="Client..." /></label>
              <label>Reference dossier<input value={filters.dossierId} onChange={e => updateFilter('dossierId', e.target.value)} placeholder="Reference..." /></label>
              <label>Chauffeur<input value={filters.chauffeur} onChange={e => updateFilter('chauffeur', e.target.value)} placeholder="Nom..." /></label>
              <label>Matricule<input value={filters.matriculeVoiture} onChange={e => updateFilter('matriculeVoiture', e.target.value)} placeholder="Vehicule..." /></label>
              <label>Produit<input value={filters.produit} onChange={e => updateFilter('produit', e.target.value)} placeholder="Nom ou reference..." /></label>
              <button className="bld-filter-reset" onClick={() => setFilters(emptyFilters)}>Reset</button>
            </section>

            <section className="bld-grid">
              <div className="bld-table-panel">
                <div className="bld-panel-title">
                  <div>
                    <h2>Liste des BL</h2>
                    <p>{fmtNumber(documents.length)} BL affiches</p>
                  </div>
                  <Search size={18} />
                </div>
                <div className="bld-table-wrap">
                  <table className="bld-table">
                    <thead>
                      <tr>
                        <th>Numero</th>
                        <th>Creation</th>
                        <th>Dossier</th>
                        <th>Client</th>
                        <th>Adresse</th>
                        <th>Chauffeur</th>
                        <th>Matricule</th>
                        <th>Produits</th>
                        <th>Statut</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map(doc => (
                        <tr key={doc._id}>
                          <td><strong>{doc.numero}</strong></td>
                          <td>{fmtDate(doc.dateCreation)}</td>
                          <td>
                            <div className="bld-dossier-cell">
                              <strong>{doc.referenceDossier || '-'}</strong>
                            </div>
                          </td>
                          <td>{doc.clientNom || '-'}</td>
                          <td className="bld-address">{doc.adresseLivraison || '-'}</td>
                          <td>{doc.chauffeur || '-'}</td>
                          <td>{doc.matriculeVoiture || '-'}</td>
                          <td>{fmtNumber(doc.quantiteTotale)}</td>
                          <td><span className={`bld-status ${doc.statutLivraison}`}>{statusLabels[doc.statutLivraison] || doc.statutLivraison}</span></td>
                          <td>
                            <div className="bld-actions">
                              <button className="view" title="Details" onClick={() => openDetailPage(doc)}><Eye size={16} /></button>
                              <button className="download" title="PDF" onClick={() => openPdf(doc)}><Download size={16} /></button>
                              {doc.statut !== 'valide' && <button className="validate" title="Valider" onClick={() => validateDocument(doc)}><CheckCircle2 size={16} /></button>}
                              {doc.statutLivraison !== 'annule' && <button className="cancel" title="Annuler" onClick={() => cancelDocument(doc)}><XCircle size={16} /></button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default BonsLivraisonDashboard;
